import { config } from '@iheartjane/config';
import type { GeocodingResult } from '@iheartjane/geolocation';
import {
  GEOCODE_ZERO_RESULTS,
  geocode,
  googleMapsLoader,
} from '@iheartjane/geolocation';
import { useEffect, useState } from 'react';

import { useCheckout } from '@jane/shared-ecomm/providers';

export interface UseValidatePickupAddress {
  address: string;
  isRequired?: boolean;
  skip?: boolean;
  street2?: string;
}

interface ValidatePickupAddress {
  addressIsValid: boolean;
  resetAddressValidation: () => void;
}

export const useValidatePickupAddress = ({
  address,
  street2,
  skip,
  isRequired = false,
}: UseValidatePickupAddress): ValidatePickupAddress => {
  const {
    dispatches: { updateCustomerPickupAddress },
    pickupAddressErrorMessage: message,
    selectedPickupAddress,
  } = useCheckout();
  const [valid, setValid] = useState(false);
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(null);
  const [googleValidatedAddress, setGoogleValidatedAddress] =
    useState<GeocodingResult | null>(null);

  const resetAddressValidation = () => {
    setValid(false);
    updateCustomerPickupAddress({
      errorMessage: null,
      googleValidatedAddress: null,
      selectedPickupAddress: null,
    });
  };

  useEffect(() => {
    if (skip) return;
    if (!address) return;
    if (address.length < 6) {
      setValid(false);
      updateCustomerPickupAddress({
        errorMessage: 'Please enter a valid address.',
        googleValidatedAddress: null,
        selectedPickupAddress: null,
      });
      return;
    }

    const validateAddress = async () => {
      if (selectedPickupAddress) {
        const location = await geocode({
          loader: () => googleMapsLoader(config.googlePlacesKey),
          searchCriteria: selectedPickupAddress,
        });

        const { zipcode, street, city, state } = location;

        if (location.googlePlaceId === googlePlaceId) return;
        resetAddressValidation();
        setGooglePlaceId(location.googlePlaceId || null);

        if (!street || !city || !state || !zipcode) {
          setValid(false);
          updateCustomerPickupAddress({
            errorMessage:
              "We don't recognize this address. Please confirm the spelling.",
            googleValidatedAddress: null,
            selectedPickupAddress: null,
          });
          return;
        }

        setValid(true);
        setGoogleValidatedAddress(location);
        updateCustomerPickupAddress({
          errorMessage: null,
          googleValidatedAddress: location,
        });
      }
    };

    validateAddress().catch((err) => {
      setValid(false);
      switch (err.message) {
        case GEOCODE_ZERO_RESULTS:
          return updateCustomerPickupAddress({
            errorMessage:
              "We can't find this address. Make sure it is spelled correctly or try adding a city, state, or zip code.",
            googleValidatedAddress: null,
            selectedPickupAddress: null,
          });
        default:
          return updateCustomerPickupAddress({
            errorMessage: 'Unable to verify address',
            googleValidatedAddress: null,
            selectedPickupAddress: null,
          });
      }
    });
  }, [selectedPickupAddress, googlePlaceId, street2]);

  const isValid = isRequired
    ? valid && googleValidatedAddress && !message
    : true;

  return {
    addressIsValid: !!isValid,
    resetAddressValidation,
  };
};
